import { css } from '@emotion/react';
import { WeaveLogo } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { TextLink } from '@frontend/design-system';
import { useFillingExperienceContext } from '../filling-experience/provider';
import { FOOTER_HEIGHT_CSS_VAR, CONTAINER_WIDTH } from './constants';
import { useTrackElementHeightVar } from './use-track-element-height';

const footerStyles = css`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.floating};
  z-index: 999;
`;

const contentContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: ${CONTAINER_WIDTH}px;
  padding: ${theme.spacing(1, 1.5)};
  margin: 0 auto;
  gap: ${theme.spacing(1)};
`;

const weaveLogoStyles = css`
  width: 80px;
  height: auto;
  display: inline;
`;

const weaveLogoContainerStyles = css`
  display: flex;
  align-items: center;
  color: ${theme.colors.neutral90};
  gap: ${theme.spacing(1)};
`;

const textLinkStyles = css`
  color: ${theme.colors.neutral90};
`;

const linksContainerStyle = css`
  display: inline;
  text-align: center;

  @media (max-width: ${breakpoints.small.min}px) {
    font-size: ${theme.fontSize(12)};
  }
`;

interface Props {
  /** Optional locationId to use directly, bypassing the FillingExperienceContext. */
  locationId?: string;
}

const FooterContent = ({ locationId }: { locationId: string }) => {
  const { t } = useTranslation('digital-forms-experience');
  const footerRef = useTrackElementHeightVar(FOOTER_HEIGHT_CSS_VAR);

  const privacyPolicyLink = (
    <TextLink css={textLinkStyles} to={`/legal/privacy-policy/${locationId}`} target='_blank'>
      {t('Privacy Policy')}
      {' | '}
    </TextLink>
  );
  const termsAndConditionsLink = (
    <TextLink css={textLinkStyles} to={`/legal/terms-conditions/${locationId}`} target='_blank'>
      {t('Terms and Conditions')}
      {' | '}
    </TextLink>
  );
  const hipaaLink = (
    <TextLink css={textLinkStyles} target='_blank' href='https://www.weavehelp.com/hc/en-us/articles/5017862597275'>
      {t('HIPAA Compliance')}
    </TextLink>
  );

  const poweredByLink = (
    <TextLink css={weaveLogoContainerStyles} href='https://getweave.com' target='_blank'>
      {t('Powered by')}
      <WeaveLogo css={weaveLogoStyles} />
    </TextLink>
  );

  return (
    <footer ref={footerRef} css={footerStyles}>
      <div css={contentContainerStyle}>
        <div css={linksContainerStyle}>
          {privacyPolicyLink}
          {termsAndConditionsLink}
          {hipaaLink}
        </div>
        {poweredByLink}
      </div>
    </footer>
  );
};

const FooterWithContext = () => {
  const { locationId } = useFillingExperienceContext();
  return <FooterContent locationId={locationId} />;
};

const Footer = ({ locationId }: Props) => {
  if (locationId !== undefined) {
    return <FooterContent locationId={locationId} />;
  }
  return <FooterWithContext />;
};
export default Footer;
